import React from 'react'
import { useTextInput } from '../../../hooks/useInput'
import { useEdit } from '../../../hooks/useEdit'
import { useTextLanguage, useDisabled } from '../../../hooks/useShared'
import { useAutocompleteDocuments } from '../../../hooks/useAutocompleteDocuments'
import { useFormContext, useWatch } from 'react-hook-form'
import { Col } from 'react-bootstrap'
import { Input } from '../../shared/Input'
import { trimLowerCase } from '../../../helpers/shared'
import { maskedMinLengthFields, maskedPhones } from '../../../constants/form'
import { StyledGroup, StyledControl } from '../../shared/Input/styled'
import { Label } from '../../shared/simple/Label'
import imgEyeOpen from '../../../assets/images/profile/eye-open.png'
import { TooltipIcon } from '../../shared/simple/TooltipIcon'
import { useParams } from 'react-router-dom'

const PartiallyHiddenInput = ({field, isGroup=''}) => {
    const uniqueName = `${field.unique_name}__id__${field.id}`;
    const { colSize } = useEdit(field);
    useAutocompleteDocuments(field, uniqueName)  

  return (
    <>
      {field && (
        <>
          {(colSize?.includes('col-before')) && (
            <Col xs={12} key={ `col_before_${field.id}` }></Col>
          )}
          <Col
            className={ colSize }
            data-module={ `module-${field.category?.field_category}` }
            data-name={ uniqueName }
            id={ `group_field_${field.id}` }
            key={ field.id }
            xs={12}
          >
              <HiddenTextInput 
                field={field}
                key={field.id}
                uniqueName={uniqueName}
                isGroup={isGroup} 
              />
          </Col>
        </>
      )}

    </>
  )
}

const HiddenTextInput = ({field, uniqueName, entryProps='', isGroup=''}) => {
  const { register, formState: { errors }, setValue, getValues } = useFormContext();
  const { isDisabled } = useDisabled(field);
  const { description, languageText } = useTextLanguage();
  const { type, maskLength, validate } = useTextInput(field, uniqueName, isGroup);
  const [isHidden, setIsHidden] = React.useState(true)
  const { actionTier } = useParams()
  const [oldValue, setOldValue] = React.useState('')

  const toggleHidden = () => {
    if (isHidden) {
      setOldValue(getValues(uniqueName))
      setValue(uniqueName, '')
    } else {
      let minAcceptedLength = 4
      if (getValues(uniqueName)?.length < minAcceptedLength) {
        setValue(uniqueName, oldValue)
      }
    }
    setIsHidden(!isHidden)
  }

  return (
    <>
      {/* Real input */}
      <Input
        className={`${isHidden ? 'd-none' : ''}`} 
        autoComplete="off"
        disabled={ isDisabled }
        field={ field }
        id={ trimLowerCase(field.id) }
        isRequiredInput={ field.required }
        label={ field[description] }
        message={ errors?.[uniqueName]?.message }
        name={ register(uniqueName).name }
        placeholder={ field[`placeholder${languageText}`] }
        register={ register(uniqueName, {
          setValueAs: val => {
            if (typeof val === 'string' && val !== 'hidden_field' && val !== 'hidden_rn' && (
              maskedMinLengthFields.some((maskedField) => uniqueName.startsWith(maskedField)) ||
              maskedPhones.some((maskedField) => uniqueName.startsWith(maskedField))
            )
            ) {
              return val.replaceAll('-', '')?.replaceAll('_', '')
            } else return val
          }
        }) }
        setValue={ setValue }
        tooltipText={ field[`tooltip${languageText}`] }
        type={ type }
        entryProps={entryProps}
        maskLength={maskLength}
        validate={validate}
        onIconClick={toggleHidden}
      />
      {/* Obscure input */}
      {isHidden && (
        <ObscureInput 
          tooltipText={ field[`tooltip${languageText}`] }
          label={ field[description] }
          isRequiredInput={ field.required }
          field={field}
          name={uniqueName}
          onClick={toggleHidden}
          action={actionTier}
        />

      )}
    </>
  )

}

const ObscureInput = ({
  field,
  type,
  placeholder,
  name='',
  label,
  id,
  tooltipText,
  isRequiredInput,
  onClick,
  action
}) => {
  let digitsToBeSeen = 4
  const value = useWatch({name: name}) || ''
  let obscuredValue = value?.slice(value.length - digitsToBeSeen).split('')
  let fill = (value?.length > digitsToBeSeen) ? '*'.repeat(value?.length - digitsToBeSeen) : '*'.repeat(value?.length)
  obscuredValue = [...fill, ...obscuredValue].join('')

  return (
    <StyledGroup className="mb-3">
      <Label htmlFor={ name || id }>
        { label }{isRequiredInput && (
          <strong className="text-danger">&nbsp;*</strong>)}
        {tooltipText && (<TooltipIcon tooltipText={ tooltipText } />)}
      </Label>
      <StyledControl
        placeholder={ placeholder }
        type={ type }
        disabled
        value={obscuredValue}

      />
      {(field?.data_type === 'partially_hidden') && (action === 'add') && (
        <span
          className="field-icon-toggle"
          onClick={onClick}
        >
          <img
            alt="Eye Open"
            className="toggle-password img-eye-open"
            src={ imgEyeOpen }
          />
        </span>
      )}
    </StyledGroup>
  )
}

export default PartiallyHiddenInput